import { useCallback } from "react"

import * as inventoryApi from "@/api/useInventory"
import { isAiDiscoveryLive } from "@/domain/user"
import { PAGE_NUMBER } from "@/helpers/filters.helper"
import { useOrderBy } from "@/hooks/useOrderBy"
import { useQueryString } from "@/hooks/useQueryString"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import * as selectors from "./useAiDiscovery.select"

import type { EntitiesAi } from "@/api/useInventory.types"
import type {
  CommonProps,
  SelectFilterFn,
  UseFiltersProps
} from "./useAiDiscovery.types"

export const useFilters = (
  props: UseFiltersProps,
  selectFn: SelectFilterFn
) => {
  const { t } = useTranslation()
  const isLive = isAiDiscoveryLive(useFeatureFlags("aiDiscovery"))
  const { type } = props
  const prefix = selectors.PREFIX_BY_TYPE[type]
  const select = useCallback(
    (response: KosmosApi.Response<Record<string, string[]>>) =>
      selectFn(response, { prefix, t, isLive }),
    [prefix, selectFn, t, isLive]
  )

  const agentsAiFiltersCtx = inventoryApi.useFiltersAi(type, {
    select
  })

  return agentsAiFiltersCtx
}

export const useQueryStringWithPrefix = (
  props: UseFiltersProps,
  selectFn: SelectFilterFn
) => {
  const prefix = selectors.PREFIX_BY_TYPE[props.type]
  // Only inventory orders the lists, so only it is asked to
  const isLive = isAiDiscoveryLive(useFeatureFlags("aiDiscovery"))
  const { response: filters } = useFilters(props, selectFn)
  const allParams = useQueryString([
    ...(isLive ? [`${prefix}orderBy`, `${prefix}ascending`] : []),
    `${prefix}${PAGE_NUMBER}`,
    ...(Array.isArray(filters?.all) ? filters.all : [])
  ])

  return Object.entries(allParams).reduce(
    (acc, [key, value]) => {
      if (key.startsWith(prefix) && value !== undefined) {
        acc[key.replace(prefix, "")] = value
      }
      return acc
    },
    {} as Record<string, string>
  )
}

export const useStatisticsAI = () => {
  const ctx = inventoryApi.useStatisticsAI({
    select: selectors.getStatisticsAi
  })

  return ctx
}

export const useEntitiesAI = <P, R>(
  type: EntitiesAi,
  selectData: (props: CommonProps) => (response: P) => R,
  selectFilter:
    | typeof selectors.getAgentsAiFilters
    | typeof selectors.getWorkflowsAiFilters
    | typeof selectors.getToolsAiFilters
    | typeof selectors.getModelsAiFilters
    | typeof selectors.getMcpServersAiFilters
    | typeof selectors.getKnowledgeSourcesAiFilters
) => {
  const params = useQueryStringWithPrefix({ type }, selectFilter)
  const { t } = useTranslation()
  const select = useCallback(
    (response: P) => selectData({ t })(response),
    [selectData, t]
  )
  const ctx = inventoryApi.useEntitiesAI(type, params, { select })

  return ctx
}

export const useAgentsAI = () => {
  const agentsAiCtx = useEntitiesAI(
    "agent",
    selectors.getAgentsAi,
    selectors.getAgentsAiFilters
  )

  return agentsAiCtx
}

export const useWorkflowsAI = () => {
  const workflowsAiCtx = useEntitiesAI(
    "workflow",
    selectors.getWorkflowsAi,
    selectors.getWorkflowsAiFilters
  )

  return workflowsAiCtx
}

export const useToolsAI = () => {
  const toolsAiCtx = useEntitiesAI(
    "tool",
    selectors.getToolsAi,
    selectors.getToolsAiFilters
  )

  return toolsAiCtx
}

export const useModels = () => {
  const modelsCtx = useEntitiesAI(
    "model",
    selectors.getModelsAi,
    selectors.getModelsAiFilters
  )

  return modelsCtx
}

export const useMcpServers = () => {
  const mcpServersCtx = useEntitiesAI(
    "mcp_server",
    selectors.getMcpServersAi,
    selectors.getMcpServersAiFilters
  )

  return mcpServersCtx
}

export const useKnowledgeSources = () => {
  const knowledgeSourcesCtx = useEntitiesAI(
    "knowledge_source",
    selectors.getKnowledgeSourcesAi,
    selectors.getKnowledgeSourcesAiFilters
  )

  return knowledgeSourcesCtx
}

// Whether any of the tenant's agents runs on an agent platform. The agents'
// source filter lists the sources of all of them, so the platform columns stay
// put while paging and filtering.
export const useHasPlatformAgents = () => {
  const { response } = useFilters(
    { type: "agent" },
    selectors.getAgentsAiFilters
  )

  return selectors.hasPlatformAgents(
    selectors.getFilterOptions(response?.source)
  )
}

// The order of a tab's table, kept in the query string under the tab's prefix.
// Only inventory orders the lists: on the experimental backend no column sorts.
export const useAiTableOrder = (prefix: string) => {
  const isLive = isAiDiscoveryLive(useFeatureFlags("aiDiscovery"))
  const { onSorterChange, ...sorter } = useOrderBy({ prefix })

  return { sorter: isLive ? sorter : undefined, onSorterChange, isLive }
}
